import {CommonModule} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  Filter,
  FilterEntity,
  FilterSelect,
  AppScope,
} from '@gatewee/types/filter';
import {FilterService} from '@gatewee/services/filter.service';
import {Client} from '@gatewee/types/client';
import {Domain} from '@gatewee/types/domain';
import {Recipient} from '@gatewee/types/recipient';
import {NgbDate, NgbDateStruct} from '@ng-bootstrap/ng-bootstrap';
import {TranslateModule} from '@ngx-translate/core';
import {TuiDay, TuiDayRange, TuiTime} from '@taiga-ui/cdk';
import {TuiDropdown, TuiHint, TuiTextfield} from '@taiga-ui/core';
import {TuiCalendarRange, TuiInputTime, TuiStatus} from '@taiga-ui/kit';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';
import {GateweeCheckboxModule} from '../gatewee-checkbox/gatewee-checkbox.module';
import {GateweeExpandChevronComponent} from '../gatewee-expand-chevron/gatewee-expand-chevron.component';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';
import {GateweeInputV2Component} from '../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeSelectV3Component} from '../gatewee-select-v3/gatewee-select-v3.component';
import {
  AsyncOption,
  GateweeSelectAsyncComponent,
} from '../gatewee-select-async/gatewee-select-async.component';

export type DateRangeTime = {
  hours: number;
  minutes: number;
};

export type DateRange = {
  from: NgbDate | null;
  to: NgbDate | null;
  fromTime?: DateRangeTime;
  toTime?: DateRangeTime;
};

export type PeriodPreset =
  | 'today'
  | 'thisWeek'
  | 'thisMonth'
  | 'last7'
  | 'last15'
  | 'last30'
  | 'custom';

export interface ActiveFilter {
  key: string;
  label: string;
  displayValue: string;
  type: 'filter' | 'dateRange';
}

@Component({
  selector: 'app-gatewee-filter',
  templateUrl: './gatewee-filter.component.html',
  styleUrls: ['./gatewee-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    GateweeIconComponent,
    GateweeCheckboxModule,
    GateweeExpandChevronComponent,
    GateweeInputV2Component,
    GateweeSelectV3Component,
    GateweeSelectAsyncComponent,
    GateweeSkeletonComponent,
    TuiCalendarRange,
    ...TuiInputTime,
    TuiTextfield,
    TuiStatus,
    ...TuiDropdown,
    ...TuiHint,
  ],
})
export class GateweeFilterComponent<T>
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() filters: Filter[] = [];
  /** When set, overrides auto-detection (empty `filters` array). */
  @Input() loading: boolean | null = null;
  /** Placeholder grid layout shown while filters are loading. */
  @Input() skeletonSlots: Array<'full' | 'half' | 'third'> = [
    'full',
    'third',
    'third',
    'third',
  ];
  @Input() enableDateRange = true;
  /** Overrides auto-detected scope for entity async selects. */
  @Input() appScope?: AppScope;
  @Input() activeToggle = false;
  @Input() syncWithUrl = false;
  @Input() tableSettingsTemplate: TemplateRef<T> | null = null;
  @Input() showReportDownload = false;
  @Input() currentPage = 1;
  @Input() currentTake = 10;

  @Output() reportDownloadClick = new EventEmitter<void>();
  @Output() filterApplied = new EventEmitter<Record<string, unknown>>();
  @Output() selectAllAcrossFilterChange = new EventEmitter<boolean>();
  @Output() pageRestored = new EventEmitter<number>();
  @Output() takeRestored = new EventEmitter<number>();
  @Output() filterRemoved = new EventEmitter<void>();
  @Output() filtersChanged = new EventEmitter<void>();
  @Output() expandedFilters = new EventEmitter<boolean>();
  @Output() filterValueChanged = new EventEmitter<{
    key: string;
    value: unknown;
  }>();

  filterPayload: Record<string, unknown> = {};
  dateRange?: DateRange;
  selectedPreset: PeriodPreset | null = null;
  customDateOpen = false;
  /** Taiga default is 400px; calendar range + time fields need more room. */
  readonly customDateDropdownMaxHeight = 560;
  settingsMenuOpen = false;
  /** Draft range while the custom period dropdown is open. */
  customCalendarValue: TuiDayRange | null = null;
  customFromTime = new TuiTime(0, 0);
  customToTime = new TuiTime(23, 59);

  readonly periodPresets: {key: PeriodPreset; i18nKey: string}[] = [
    {key: 'today', i18nKey: 'common.filters.period.today'},
    {key: 'thisWeek', i18nKey: 'common.filters.period.thisWeek'},
    {key: 'thisMonth', i18nKey: 'common.filters.period.thisMonth'},
    {key: 'last7', i18nKey: 'common.filters.period.last7'},
    {key: 'last15', i18nKey: 'common.filters.period.last15'},
    {key: 'last30', i18nKey: 'common.filters.period.last30'},
  ];

  readonly periodSkeletonItems = Array.from({length: 7});

  private textInput$ = new Subject<void>();
  private textInputFilter$ = new Subject<Filter>();
  private destroy$ = new Subject<void>();
  private urlSyncInitialized = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private readonly filterService: FilterService,
  ) {}

  ngOnInit(): void {
    this.textInput$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.applyFilters();
      });

    this.textInputFilter$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(filter => {
        this.filterValueChanged.emit({key: filter.key, value: filter.value});
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.syncWithUrl &&
      !this.urlSyncInitialized &&
      changes['filters']?.currentValue?.length > 0
    ) {
      this.restoreFiltersFromUrl();
      this.urlSyncInitialized = true;
    }
    if (
      (changes['currentPage'] || changes['currentTake']) &&
      this.syncWithUrl &&
      this.urlSyncInitialized
    ) {
      this.syncPaginationToUrl(this.currentPage, this.currentTake);
    }
  }

  ngAfterViewInit(): void {
    if (
      this.syncWithUrl &&
      !this.urlSyncInitialized &&
      this.filters.length > 0
    ) {
      this.restoreFiltersFromUrl();
      this.urlSyncInitialized = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showSkeleton(): boolean {
    return this.loading ?? this.filters.length === 0;
  }

  get isFilterPanelOpen(): boolean {
    return this.activeToggle || this.showSkeleton;
  }

  get activeFilterCount(): number {
    return this.getActiveFilters().length;
  }

  getActiveFilters(): ActiveFilter[] {
    const active: ActiveFilter[] = [];

    for (const filter of this.filters) {
      if (
        filter.value !== undefined &&
        filter.value !== null &&
        filter.value !== ''
      ) {
        active.push({
          key: filter.key,
          label: filter.label,
          displayValue: this.getFilterDisplayValue(filter),
          type: 'filter',
        });
      }
    }

    if (this.enableDateRange && this.dateRange?.from && this.dateRange?.to) {
      const from = this.dateRange.from;
      const to = this.dateRange.to;
      active.push({
        key: '__dateRange',
        label: '',
        displayValue: `${this.formatDateRangeEndpoint(
          from,
          this.dateRange.fromTime,
        )} - ${this.formatDateRangeEndpoint(to, this.dateRange.toTime)}`,
        type: 'dateRange',
      });
    }

    return active;
  }

  getSpanClass(filter: Filter): string {
    return this.getSkeletonSpanClass(filter.span ?? 'third');
  }

  getSkeletonSpanClass(span: 'full' | 'half' | 'third'): string {
    if (span === 'full') return 'gw-col-span-12';
    if (span === 'half') return 'gw-col-span-6';
    return 'gw-col-span-4';
  }

  toggleExpanded(): void {
    this.activeToggle = !this.activeToggle;
    this.expandedFilters.emit(this.activeToggle);
  }

  onReportDownloadClick(event: Event): void {
    event.stopPropagation();
    this.reportDownloadClick.emit();
  }

  onSelectAllAcrossFilterToggle(value: boolean): void {
    this.selectAllAcrossFilterChange.emit(value);
  }

  onTextInput(filter?: Filter): void {
    this.textInput$.next();
    if (filter) {
      this.textInputFilter$.next(filter);
    }
  }

  onSelectChange(filter?: Filter): void {
    if (filter) {
      this.filterValueChanged.emit({key: filter.key, value: filter.value});
    }
    this.applyFilters();
  }

  isAsyncEntitySelect(filter: Filter): filter is FilterSelect & {
    entity: FilterEntity;
  } {
    return filter.type === 'select' && !!filter.entity;
  }

  get resolvedAppScope(): AppScope {
    if (this.appScope) {
      return this.appScope;
    }
    if (this.filters.some(f => f.entity === 'domain')) {
      return 'admin';
    }
    if (this.filters.some(f => f.entity === 'recipient')) {
      return 'domain';
    }
    return 'recipient';
  }

  getEntityScopeKey(filter: FilterSelect): string {
    return JSON.stringify(
      this.filterService.buildEntityListOptions(
        filter,
        this.filters,
        this.resolvedAppScope,
      ),
    );
  }

  trackEntityScopeKey(_: number, key: string): string {
    return key;
  }

  getEntitySource(filter: FilterSelect) {
    return this.filterService.getEntityCrudSource(
      filter.entity!,
      this.resolvedAppScope,
    );
  }

  getEntityListOptions(filter: FilterSelect): Record<string, unknown> {
    return this.filterService.buildEntityListOptions(
      filter,
      this.filters,
      this.resolvedAppScope,
    );
  }

  toEntityOption = (
    entity: Domain | Recipient | Client,
  ): AsyncOption<number> => ({
    value: entity.id,
    label: entity.name ?? `#${entity.id}`,
    meta: entity,
  });

  entityFilterModelValue(filter: FilterSelect): number | null {
    const v = filter.value;
    if (v === '' || v === null || v === undefined) {
      return null;
    }
    return Number(v);
  }

  entityIdFromValue = (value: unknown): number => Number(value);

  onAsyncEntityModelChange(filter: FilterSelect, value: number | null): void {
    filter.value = value ?? '';
    if (!value) {
      filter.selectedLabel = undefined;
      this.filterValueChanged.emit({key: filter.key, value: filter.value});
      this.applyFilters();
    }
  }

  onAsyncEntitySelectChange(
    filter: FilterSelect,
    option: AsyncOption<number> | null,
  ): void {
    filter.selectedLabel = option?.label;
    filter.value =
      option?.value === null || option?.value === undefined ? '' : option.value;
    this.filterValueChanged.emit({key: filter.key, value: filter.value});
    this.applyFilters();
  }

  selectPreset(preset: PeriodPreset): void {
    this.selectedPreset = preset;
    this.customDateOpen = false;
    if (preset !== 'custom') {
      this.dateRange = this.computeDateRange(preset);
      this.applyFilters();
    }
  }

  onCustomDateDropdownOpenChange(open: boolean): void {
    this.customDateOpen = open;
    if (open) {
      this.syncCustomDraftFromDateRange();
    }
    this.cdr.markForCheck();
  }

  onCustomCalendarChange(value: TuiDayRange | null): void {
    this.customCalendarValue = value;
    this.cdr.markForCheck();
  }

  applyCustomDateRange(): void {
    if (!this.customCalendarValue) {
      return;
    }

    this.dateRange = {
      from: this.tuiDayToNgb(this.customCalendarValue.from),
      to: this.tuiDayToNgb(this.customCalendarValue.to),
      fromTime: {
        hours: this.customFromTime.hours,
        minutes: this.customFromTime.minutes,
      },
      toTime: {
        hours: this.customToTime.hours,
        minutes: this.customToTime.minutes,
      },
    };
    this.selectedPreset = 'custom';
    this.customDateOpen = false;
    this.applyFilters();
  }

  removeFilter(key: string): void {
    if (key === '__dateRange') {
      this.dateRange = undefined;
      this.selectedPreset = null;
    } else {
      const filter = this.filters.find(f => f.key === key);
      if (filter) {
        filter.value = filter.type === 'date' ? null : '';
        filter.selectedLabel = undefined;
        this.filterValueChanged.emit({key: filter.key, value: filter.value});
      }
    }
    this.applyFilters();
    this.cdr.markForCheck();
  }

  removeAllFilters(): void {
    this.filters = this.filters.map(filter => ({
      ...filter,
      value: filter.type === 'date' ? null : '',
      selectedLabel: undefined,
    }));
    this.dateRange = undefined;
    this.selectedPreset = null;
    this.customDateOpen = false;
    this.syncUrlParams({}, true);
    this.filtersChanged.emit();
    this.filterRemoved.emit();
  }

  trackByFilter(index: number, filter: Filter): string {
    return filter?.key ?? `filter-${index}`;
  }

  trackBySkeletonSlot(index: number, span: 'full' | 'half' | 'third'): string {
    return `${span}-${index}`;
  }

  trackByPreset(
    index: number,
    preset: {key: PeriodPreset; i18nKey: string},
  ): string {
    return preset.key;
  }

  applyFilters(isUrlRestore = false): void {
    this.filterPayload = {
      ...this.filters.reduce((acc: Record<string, unknown>, filter) => {
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ''
        ) {
          acc[filter.key] = this.formatFilterValue(filter);
        }
        return acc;
      }, {}),
    };

    if (this.enableDateRange && this.dateRange) {
      const {from, to, fromTime, toTime} = this.dateRange;
      if (from && to) {
        this.filterPayload['options.startDate'] = this.formatDateTimeForPayload(
          from,
          fromTime ?? {hours: 0, minutes: 0},
        );
        this.filterPayload['options.endDate'] = this.formatDateTimeForPayload(
          to,
          toTime ?? {hours: 23, minutes: 59},
        );
      }
    }

    this.syncUrlParams(this.filterPayload, !isUrlRestore);
    if (!isUrlRestore) {
      this.filtersChanged.emit();
    }
    this.filterApplied.emit(this.filterPayload);
    this.cdr.markForCheck();
  }

  // ========== URL SYNC ==========

  private readonly allowedTakeValues = [10, 25, 50, 100];

  private restoreFiltersFromUrl(): void {
    const params = this.route.snapshot.queryParams;
    if (!params || Object.keys(params).length === 0) return;

    let hasRestoredFilterContent = false;
    let shouldApply = false;

    const takeNum = Math.round(Number(params['take']));
    if (
      Number.isFinite(takeNum) &&
      this.allowedTakeValues.includes(takeNum) &&
      takeNum !== 10
    ) {
      this.takeRestored.emit(takeNum);
      shouldApply = true;
    }

    const pageNum = Math.round(Number(params['page']));
    if (Number.isFinite(pageNum) && pageNum > 1) {
      this.pageRestored.emit(pageNum);
      shouldApply = true;
    }

    for (const filter of this.filters) {
      const urlValue = params[filter.key];
      if (urlValue !== undefined && urlValue !== '') {
        filter.value = urlValue;
        hasRestoredFilterContent = true;
        shouldApply = true;
      }
    }

    // Restore date range from URL
    const startDate = params['options.startDate'];
    const endDate = params['options.endDate'];
    if (startDate && endDate) {
      const parsedFrom = this.parseDateTimeParam(String(startDate));
      const parsedTo = this.parseDateTimeParam(String(endDate));
      if (parsedFrom && parsedTo) {
        this.dateRange = {
          from: parsedFrom.date,
          to: parsedTo.date,
          fromTime: parsedFrom.time,
          toTime: parsedTo.time,
        };
        this.selectedPreset = 'custom';
        hasRestoredFilterContent = true;
        shouldApply = true;
      }
    }

    if (hasRestoredFilterContent) {
      this.activeToggle = true;
      this.cdr.markForCheck();
    }

    if (shouldApply) {
      // Defer the apply to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => this.applyFilters(true), 0);
    }
  }

  private syncPaginationToUrl(page: number, take: number): void {
    if (!this.syncWithUrl) return;
    const safePage = Math.max(1, Math.round(Number(page)) || 1);
    const safeTake = Math.round(Number(take)) || 10;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: safePage > 1 ? String(safePage) : null,
        take: safeTake !== 10 ? String(safeTake) : null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private syncUrlParams(
    payload: Record<string, unknown>,
    clearPagination = false,
  ): void {
    if (!this.syncWithUrl) return;

    const queryParams: Record<string, string | null> = {};

    // Build all known filter keys so we can clear unused ones
    const allFilterKeys = this.filters.map(f => f.key);
    if (this.enableDateRange) {
      allFilterKeys.push('options.startDate', 'options.endDate');
    }

    for (const key of allFilterKeys) {
      const value = payload[key];
      queryParams[key] =
        value !== undefined && value !== null && value !== ''
          ? String(value)
          : null;
    }

    if (clearPagination) {
      queryParams['page'] = null;
      queryParams['take'] = null;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // ========== PRIVATE HELPERS ==========

  private formatFilterValue(filter: Filter): unknown {
    if (filter.type === 'date' && filter.value) {
      if (typeof filter.value === 'string') {
        return filter.value;
      }
      const {year, month, day} = filter.value as NgbDateStruct;
      return `${year}-${month.toString().padStart(2, '0')}-${day
        .toString()
        .padStart(2, '0')}`;
    }
    return filter.value;
  }

  private getFilterDisplayValue(filter: Filter): string {
    if (filter.type === 'select' && filter.value) {
      if (filter.selectedLabel) {
        return filter.selectedLabel;
      }
      const data = (filter as FilterSelect).data;
      if (data) {
        for (const item of data) {
          if ('value' in item && item.value === filter.value) {
            return item.label;
          }
        }
      }
    }
    if (filter.type === 'date' && filter.value) {
      if (typeof filter.value === 'string') return filter.value;
      const {year, month, day} = filter.value as NgbDateStruct;
      return `${day.toString().padStart(2, '0')}/${month
        .toString()
        .padStart(2, '0')}/${year}`;
    }
    return String(filter.value ?? '');
  }

  private formatNgbDate(date: NgbDate): string {
    return `${date.day.toString().padStart(2, '0')}/${date.month
      .toString()
      .padStart(2, '0')}/${date.year}`;
  }

  private formatDateRangeEndpoint(date: NgbDate, time?: DateRangeTime): string {
    const base = this.formatNgbDate(date);
    if (!time) {
      return base;
    }
    return `${base} ${time.hours.toString().padStart(2, '0')}:${time.minutes
      .toString()
      .padStart(2, '0')}`;
  }

  private formatDateTimeForPayload(date: NgbDate, time: DateRangeTime): string {
    return `${date.year}-${date.month.toString().padStart(2, '0')}-${date.day
      .toString()
      .padStart(2, '0')}T${time.hours
      .toString()
      .padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}:00`;
  }

  private parseDateTimeParam(
    raw: string,
  ): {date: NgbDate; time: DateRangeTime} | null {
    const trimmed = raw.trim();
    if (!trimmed) {
      return null;
    }

    const dateTimeMatch = trimmed.match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::\d{2})?)?/,
    );
    if (!dateTimeMatch) {
      return null;
    }

    const [, y, m, d, hh, mm] = dateTimeMatch;
    const year = Number(y);
    const month = Number(m);
    const day = Number(d);
    if (
      !Number.isFinite(year) ||
      !Number.isFinite(month) ||
      !Number.isFinite(day)
    ) {
      return null;
    }

    return {
      date: new NgbDate(year, month, day),
      time: {
        hours: hh !== undefined ? Number(hh) : 0,
        minutes: mm !== undefined ? Number(mm) : 0,
      },
    };
  }

  private syncCustomDraftFromDateRange(): void {
    if (this.dateRange?.from && this.dateRange?.to) {
      this.customCalendarValue = new TuiDayRange(
        this.ngbToTuiDay(this.dateRange.from),
        this.ngbToTuiDay(this.dateRange.to),
      );
      const fromTime = this.dateRange.fromTime ?? {hours: 0, minutes: 0};
      const toTime = this.dateRange.toTime ?? {hours: 23, minutes: 59};
      this.customFromTime = new TuiTime(fromTime.hours, fromTime.minutes);
      this.customToTime = new TuiTime(toTime.hours, toTime.minutes);
      return;
    }

    this.customCalendarValue = null;
    this.customFromTime = new TuiTime(0, 0);
    this.customToTime = new TuiTime(23, 59);
  }

  private ngbToTuiDay(date: NgbDate): TuiDay {
    return new TuiDay(date.year, date.month - 1, date.day);
  }

  private tuiDayToNgb(day: TuiDay): NgbDate {
    return new NgbDate(day.year, day.month + 1, day.day);
  }

  private computeDateRange(preset: PeriodPreset): DateRange {
    const today = new Date();
    let from: Date;
    const to: Date = today;

    switch (preset) {
      case 'today':
        from = today;
        break;
      case 'thisWeek': {
        const day = today.getDay();
        const diff = day === 0 ? 6 : day - 1;
        from = new Date(today);
        from.setDate(today.getDate() - diff);
        break;
      }
      case 'thisMonth':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'last7':
        from = new Date(today);
        from.setDate(today.getDate() - 7);
        break;
      case 'last15':
        from = new Date(today);
        from.setDate(today.getDate() - 15);
        break;
      case 'last30':
        from = new Date(today);
        from.setDate(today.getDate() - 30);
        break;
      default:
        from = today;
    }

    return {
      from: new NgbDate(
        from.getFullYear(),
        from.getMonth() + 1,
        from.getDate(),
      ),
      to: new NgbDate(to.getFullYear(), to.getMonth() + 1, to.getDate()),
      fromTime: {hours: 0, minutes: 0},
      toTime: {hours: 23, minutes: 59},
    };
  }
}
